import Head from "next/head";
import styles from "./dmd2.module.scss";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import classNames from "classnames";

import "@fontsource/inter";
import "@fontsource/inter/400.css"; // Specify weight
import "@fontsource/inter/600.css"; // Specify weight

import CheckIcon from "@mui/icons-material/Check";

import {
  getCountryPhonePrefix,
  getInvalidBrandQueryString,
  getPhoneNumbers,
} from "@/brands";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import FullPageBranded from "@/components/molecules/fullpage-branded/fullpage";
import { Websites } from "@/data/themes";
import { GetServerSidePropsContext } from "next";
import setCsrfCookie from "@/helpers/csrfHelper";
import { client, server } from "@/helpers/logger";
import FormControlLabel from "@/components/atoms/form-label/label";
import Button from "@/components/atoms/button/button";
import SebkortForm from "@/components/molecules/form/form";
import { getInvalidCountryQueryString } from "@/helpers/support-email";
import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Paper,
} from "@mui/material";
import TextField from "@/components/atoms/text-field/text-field";
import { formatTelNumber } from "@/helpers/phonenumberHelper";
import PageSetup from "@/helpers/PageSetup";

let lastLog = "";

export default function DMD2Page(
  props: Readonly<{ locale: string; csrfToken: string }>,
) {
  const { t, brand, country, mailto, isDevelopment } = PageSetup(props);

  const router = useRouter();
  const { query, locales: routerLocales } = router;

  // Build path from stable values to avoid SSR/client hydration mismatch
  // (router.asPath differs between server and client with i18n).
  // brand is left out on purpose, it travels in a hidden form field instead.
  const queryString = new URLSearchParams(
    Object.entries(query).reduce(
      (acc, [k, v]) => {
        if (k !== "brand" && v !== undefined)
          acc[k] = Array.isArray(v) ? v[0] : v;
        return acc;
      },
      {} as Record<string, string>,
    ),
  ).toString();
  const pathString =
    "/" +
    props.locale +
    router.pathname +
    (queryString ? "?" + queryString : "");

  const brandStr = Array.isArray(brand) ? brand[0] : brand;
  const countryStr = Array.isArray(country) ? country[0] : country;
  const openHoursKey =
    brandStr === "saseurobonusmc" &&
    (countryStr === "da" || props.locale === "da")
      ? "openWeekdaysHours-sas-da"
      : "openWeekdaysHours";

  // normalize locales (fallback to empty array if undefined)
  const localesArr: readonly string[] = routerLocales ?? [];

  const brandErrorString = getInvalidBrandQueryString(brandStr, props.locale);
  if (brandErrorString.length) return brandErrorString;

  const countryErrorString = getInvalidCountryQueryString(
    countryStr,
    localesArr,
  );

  if (countryErrorString.length) return countryErrorString;

  const WITHDRAWAL_DEFAULT_EMAIL = "noreply-webform@sebkort.com";
  const mailtoStr = Array.isArray(mailto) ? mailto[0] : mailto;
  const supportEmail = mailtoStr || WITHDRAWAL_DEFAULT_EMAIL;

  if (mailtoStr && mailtoStr !== lastLog) {
    client.log("Withdrawal mailto override", mailtoStr);
    lastLog = mailtoStr;
  }

  const phoneLang =
    props.locale === "en" ? countryStr || props.locale : props.locale;
  const phoneNumbers = getPhoneNumbers({
    brand: `${brand}`,
    lang: phoneLang,
  });
  const phonePrefix = getCountryPhonePrefix({
    brand: `${brand}`,
    lang: phoneLang,
  });

  return (
    <>
      <Head>
        <title>{t("Cancel your agreement")}</title>

        <meta name="robots" content="noindex, nofollow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {isDevelopment ? <link rel="icon" href="/favicon.ico" /> : null}
      </Head>

      <FullPageBranded
        useHeader
        locale={props.locale}
        brand={`${brandStr}`}
        activeStep={0}
        country={country}
        classNameContent={styles.content}
        website={Websites.DMD2}
      >
        <main className={classNames([styles.main])}>
          <Typography
            variant="h4"
            component="h1"
            className={classNames([styles.h1])}
            tabIndex={-1}
          >
            {t("Cancel your agreement")}
          </Typography>
          <div className={classNames([styles.mainBody])}>
            <Typography
              component="p"
              variant="h6"
              className={classNames([styles.h2])}
            >
              {t(
                "Would you like to cancel your agreement? No problem, you have 14 days to do so. Call us or submit a request below.",
              )}
            </Typography>

            <Alert
              severity="warning"
              role="presentation"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  fill="none"
                  viewBox="0 0 36 36"
                >
                  <path
                    fill="#fff1c2"
                    d="M0 8a8 8 0 0 1 8-8h20a8 8 0 0 1 8 8v20a8 8 0 0 1-8 8H8a8 8 0 0 1-8-8z"
                  />
                  <g clipPath="url(#a)">
                    <path
                      stroke="#1e1e1e"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M26.333 22.1v2.5a1.667 1.667 0 0 1-1.816 1.667 16.5 16.5 0 0 1-7.192-2.559 16.25 16.25 0 0 1-5-5 16.5 16.5 0 0 1-2.558-7.225 1.667 1.667 0 0 1 1.658-1.816h2.5A1.666 1.666 0 0 1 15.59 11.1c.106.8.302 1.586.584 2.342A1.67 1.67 0 0 1 15.8 15.2l-1.059 1.058a13.33 13.33 0 0 0 5 5L20.8 20.2a1.67 1.67 0 0 1 1.758-.375c.756.282 1.542.478 2.342.583a1.666 1.666 0 0 1 1.433 1.692"
                    />
                  </g>
                  <defs>
                    <clipPath id="a">
                      <path fill="#fff" d="M8 8h20v20H8z" />
                    </clipPath>
                  </defs>
                </svg>
              }
            >
              <Typography variant="body1">
                {t("Call us and we will handle your matter immediately.")}
              </Typography>
              <Typography className={styles.phoneNumberWrap} variant="body1">
                {Object.keys(phoneNumbers).map((key) => {
                  const phoneObj = phoneNumbers[key];

                  return (
                    <span key={key}>
                      <a
                        href={`tel:${formatTelNumber(phoneObj.value, phonePrefix)}`}
                        key={key}
                        className={styles.phoneNumberLink}
                      >
                        {phoneObj.value}
                      </a>
                      {Object.keys(phoneNumbers).length > 1
                        ? ` ${phoneObj.label}`
                        : ""}
                    </span>
                  );
                })}
              </Typography>
              <Typography
                variant="body2"
                className={classNames([styles.openHours])}
              >
                {t(openHoursKey)}
              </Typography>
            </Alert>

            <Divider>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("You can also submit a written request")}
              </Typography>
            </Divider>

            <Paper elevation={0} sx={{ p: { xs: 0, sm: 3 } }}>
              <SebkortForm
                encType="multipart/form-data"
                method="post"
                action="/api/sendDmd2Mail"
                useCustomValidation
              >
                {({ fieldValidity }) => (
                  <Stack
                    sx={{
                      gap: 3,
                    }}
                  >
                    <input
                      type="hidden"
                      name="csrf_token"
                      value={encodeURIComponent(props.csrfToken)}
                    />
                    <input type="hidden" name="mailto" value={supportEmail} />
                    <input type="hidden" name="locale" value={props.locale} />
                    <input type="hidden" name="brand" value={brandStr ?? ""} />
                    <input type="hidden" name="path" value={pathString} />
                    <Typography variant="body1">
                      {t(
                        "You can start your request in the form, but in order to complete the matter, we will contact you.",
                      )}
                    </Typography>

                    <FormControlLabel
                      labelPlacement="top"
                      className={classNames([])}
                      control={
                        <TextField
                          required
                          defaultValue={""}
                          autoComplete="off"
                          placeholder={t("First name") ?? ""}
                          inputProps={{
                            onBlur: (e) =>
                              (e.target.value = e.target.value.trim()),
                            minLength: 1,
                            maxLength: 128,
                          }}
                          name="customer-information[firstName]"
                          error={
                            fieldValidity["customer-information[firstName]"] ===
                            false
                          }
                          helperText={
                            fieldValidity["customer-information[firstName]"] ===
                            true
                              ? null
                              : t("Fill in your first name.") ||
                                "Fill in your first name."
                          }
                        />
                      }
                      label={t("First name")}
                    />
                    <FormControlLabel
                      labelPlacement="top"
                      className={classNames([])}
                      control={
                        <TextField
                          required
                          defaultValue={""}
                          autoComplete="off"
                          placeholder={t("Last name") ?? undefined}
                          inputProps={{
                            onBlur: (e) =>
                              (e.target.value = e.target.value.trim()),
                            minLength: 1,
                            maxLength: 128,
                          }}
                          name="customer-information[lastName]"
                          error={
                            fieldValidity["customer-information[lastName]"] ===
                            false
                          }
                          helperText={
                            fieldValidity["customer-information[lastName]"] ===
                            true
                              ? null
                              : t("Fill in your last name.") ||
                                "Fill in your last name."
                          }
                        />
                      }
                      label={t("Last name")}
                    />
                    <FormControlLabel
                      labelPlacement="top"
                      className={classNames([])}
                      control={
                        <TextField
                          type="email"
                          required
                          defaultValue={""}
                          autoComplete="off"
                          inputProps={{
                            onBlur: (e) =>
                              (e.target.value = e.target.value.trim()),
                            pattern: String.raw`.*@[A-Za-z0-9\.\-]+\.[A-Za-z]{2,}`,
                            maxLength: 128,
                          }}
                          name="customer-information[email]"
                          helperText={
                            fieldValidity["customer-information[email]"] ===
                            true
                              ? t("Enter the same email as in the application.")
                              : t("Please enter a valid email address.")
                          }
                          error={
                            fieldValidity["customer-information[email]"] ===
                            false
                          }
                        />
                      }
                      label={t("E-mail address")}
                    />
                    <FormControlLabel
                      labelPlacement="top"
                      control={
                        <TextField
                          required
                          name="customer-information[telephone-number]"
                          type="tel"
                          helperText={
                            fieldValidity[
                              "customer-information[telephone-number]"
                            ] === true
                              ? t(
                                  "Enter the same phone number as in the application.",
                                )
                              : t(
                                  "Enter phone number with country code, e.g. +46 76 xxx xxxx",
                                )
                          }
                          error={
                            fieldValidity[
                              "customer-information[telephone-number]"
                            ] === false
                          }
                          inputProps={{
                            maxLength: 128,
                            onBlur: (e) =>
                              (e.target.value = e.target.value
                                .trim()
                                .replaceAll(/\s/g, "")
                                .replaceAll(/[a-c]/gi, "2")
                                .replaceAll(/[d-f]/gi, "3")
                                .replaceAll(/[g-i]/gi, "4")
                                .replaceAll(/[j-l]/gi, "5")
                                .replaceAll(/[m-o]/gi, "6")
                                .replaceAll(/[p-s]/gi, "7")
                                .replaceAll(/[t-v]/gi, "8")
                                .replaceAll(/[w-z]/gi, "9")
                                .replaceAll("-", "")
                                .replaceAll(/(\+\d{2})0/g, "$1")),
                          }}
                        />
                      }
                      label={t("Mobile/Cell Phone number")}
                    />
                    <FormControlLabel
                      labelPlacement="top"
                      className={classNames([])}
                      control={
                        <TextField
                          required
                          defaultValue={""}
                          autoComplete="off"
                          placeholder={
                            t("Contract number or product name") ?? undefined
                          }
                          inputProps={{
                            onBlur: (e) =>
                              (e.target.value = e.target.value.trim()),
                            minLength: 2,
                            maxLength: 128,
                          }}
                          name="customer-information[product-or-agreement]"
                          helperText={
                            fieldValidity[
                              "customer-information[product-or-agreement]"
                            ] === false
                              ? t(
                                  "Fill in information about contract number or product name.",
                                )
                              : t(
                                  "Fill in the agreement number or write the product name.",
                                )
                          }
                          error={
                            fieldValidity[
                              "customer-information[product-or-agreement]"
                            ] === false
                          }
                        />
                      }
                      label={t("Help us find the right product")}
                    />
                    <Alert
                      role="presentation"
                      severity="success"
                      variant="standard"
                      icon={<CheckIcon />}
                    >
                      {t(
                        "We will send you a confirmation via email and then contact you to complete your case.",
                      )}
                    </Alert>
                    <FormControl
                      error={
                        fieldValidity["customer-information[confirmation]"] ===
                        false
                      }
                      component="fieldset"
                      variant="standard"
                    >
                      <FormControlLabel
                        sx={{
                          alignItems: "flex-start",
                          gap: "0.75rem",
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            fontWeight: 400,
                          },
                        }}
                        control={
                          <Checkbox
                            required
                            name="customer-information[confirmation]"
                            sx={{ p: 0 }}
                          />
                        }
                        label={t(
                          "I confirm that the agreement was signed within the last 14 days. (30 days if the request concerns an insurance policy)",
                        )}
                      />
                      {fieldValidity["customer-information[confirmation]"] ===
                        false && (
                        <FormHelperText
                          sx={{
                            ml: "calc(24px + 0.75rem)",
                            mt: "0.5rem",
                            fontSize: "1rem",
                            lineHeight: "1.4",
                          }}
                        >
                          {t(
                            "You need to confirm this in order to submit your request. The right of withdrawal applies for 14 days from the date of the contract.",
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <Button variant="contained" type="submit">
                      {t("Send my request")}
                    </Button>
                  </Stack>
                )}
              </SebkortForm>
            </Paper>
          </div>
        </main>

        <footer className={classNames([styles.footer])}></footer>
      </FullPageBranded>
    </>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, req } = context;

  const csrfToken = setCsrfCookie(context);
  server.logPageVisit(req);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["common", "footer"])),
      locale,
      csrfToken,
    },
  };
}
